import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import {
    TooltipProvider,
    TooltipRoot,
    TooltipTrigger,
    TooltipContent,
} from "@component/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import { Calendar } from "@component/calendar";
import { Switch } from "@component/switch";
import { InfoIcon, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@util/tailwind";
import { getCompanyInfo } from "@service/user";
import { setupSubscription, getSubscriptionPaidPackages, SubscriptionPackageItem } from "@service/subscription";
import { COUNTRY_CODES } from "@component/country-codes-data";
import { useSnackbar } from "@hook/use-snackbar";

interface ActiveSubscriptionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (data: ActiveSubscriptionData) => void;
    companyId: string;
    companyName: string;
    ownerName: string;
    ownerEmail: string;
}

// Display label per package platform (mirrors the admin Subscription Packages
// page: "agent" surfaces as "Operator"). Unknown/absent platform → no label.
const PLAN_PLATFORM_LABEL: Record<string, string> = {
    recruiter: "Recruiter",
    training: "Training",
    agent: "Operator",
};

export interface ActiveSubscriptionData {
    companyName: string;
    billingName: string;
    billingEmail: string;
    streetAddress: string;
    unitSuiteFloor: string;
    city: string;
    stateProvince: string;
    postcodeZip: string;
    country: string;
    invoiceCurrency: string;
    subscriptionPlan: string;
    startDate: Date | undefined;
    isSubscriptionPaid: boolean;
    invoiceNumber: string;
    invoiceDate: Date | undefined;
}

export function ActiveSubscriptionDialog({
    isOpen,
    onClose,
    onConfirm,
    companyId,
    companyName,
    ownerName,
    ownerEmail,
}: ActiveSubscriptionDialogProps) {
    const snackbar = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [packages, setPackages] = useState<SubscriptionPackageItem[]>([]);
    const [formData, setFormData] = useState<ActiveSubscriptionData>({
        companyName: companyName,
        billingName: ownerName,
        billingEmail: ownerEmail,
        streetAddress: "",
        unitSuiteFloor: "",
        city: "",
        stateProvince: "",
        postcodeZip: "",
        country: "",
        invoiceCurrency: "MYR",
        subscriptionPlan: "",
        startDate: new Date(),
        isSubscriptionPaid: false,
        invoiceNumber: "",
        invoiceDate: undefined,
    });

    useEffect(() => {
        if (!isOpen) return;

        getSubscriptionPaidPackages().then((resp) => {
            if (resp.status === 200) {
                setPackages(resp.data.data);
            }
        });

        if (!companyId) return;

        getCompanyInfo(companyId).then((resp) => {
            if (resp.status === 200) {
                const billing = resp.data.data?.company_metadata?.billing_info;
                if (billing) {
                    setFormData((prev) => ({
                        ...prev,
                        companyName: billing.billing_company || companyName,
                        billingName: billing.billing_name || ownerName,
                        billingEmail: billing.billing_email || ownerEmail,
                        streetAddress: billing.billing_address || "",
                        unitSuiteFloor: billing.billing_unit_floor || "",
                        city: billing.billing_city || "",
                        stateProvince: billing.billing_state_province || "",
                        postcodeZip: billing.billing_zip_postal || "",
                        country: billing.billing_country || "",
                    }));
                }
            }
        });
    }, [isOpen]);

    const handleSubmit = () => {
        if (
            !formData.streetAddress ||
            !formData.city ||
            !formData.stateProvince ||
            !formData.postcodeZip ||
            !formData.country ||
            !formData.invoiceCurrency ||
            !formData.subscriptionPlan ||
            !formData.startDate
        ) {
            snackbar.showError("Please fill in all required fields.");
            return;
        }

        setIsSubmitting(true);
        setupSubscription({
            company_id: companyId,
            billing_name: formData.billingName,
            billing_company: formData.companyName,
            billing_email: formData.billingEmail,
            billing_address: formData.streetAddress,
            billing_unit_floor: formData.unitSuiteFloor,
            billing_city: formData.city,
            billing_state_province: formData.stateProvince,
            billing_zip_postal: formData.postcodeZip,
            billing_country: formData.country,
            payment_currency: formData.invoiceCurrency,
            selected_package_id: formData.subscriptionPlan,
            subscription_started_at: format(formData.startDate!, "yyyy-MM-dd"),
            is_subscription_paid: formData.isSubscriptionPaid,
            invoice_number: formData.invoiceNumber.trim(),
            invoice_date: formData.invoiceDate ? format(formData.invoiceDate, "yyyy-MM-dd") : "",
        }).then((resp) => {
            if (resp.status === 200 || resp.status === 201) {
                snackbar.showSuccess("Subscription activated successfully.");
                onConfirm(formData);
                onClose();
            }
        }).catch((error) => {
            const message = error?.response?.data?.message || "Failed to activate subscription.";
            snackbar.showError(message);
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Activate Subscription</DialogTitle>
                    <DialogDescription>
                        Assign a subscription plan and configure billing details for this workspace.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-8 py-4">
                    {/* Billing Details */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <Label className="text-sm font-medium">Billing Details</Label>
                            <TooltipProvider>
                                <TooltipRoot>
                                    <TooltipTrigger asChild>
                                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                        <p>These will default to the Workspace Owner details but can still be edited before switching access.</p>
                                    </TooltipContent>
                                </TooltipRoot>
                            </TooltipProvider>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="companyName" className="text-xs text-muted-foreground">
                                    Billing Company
                                </Label>
                                <Input
                                    id="companyName"
                                    value={formData.companyName}
                                    onChange={(e) =>
                                        setFormData({ ...formData, companyName: e.target.value })
                                    }
                                    placeholder="TechCorp Solutions"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="billingName" className="text-xs text-muted-foreground">
                                    Billing Name
                                </Label>
                                <Input
                                    id="billingName"
                                    value={formData.billingName}
                                    onChange={(e) =>
                                        setFormData({ ...formData, billingName: e.target.value })
                                    }
                                    placeholder="TechCorp Solutions"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="billingEmail" className="text-xs text-muted-foreground">
                                    Billing Email
                                </Label>
                                <Input
                                    id="billingEmail"
                                    value={formData.billingEmail}
                                    onChange={(e) =>
                                        setFormData({ ...formData, billingEmail: e.target.value })
                                    }
                                    placeholder="admin@techcorp.com"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Billing Address */}
                    <div className="space-y-4">
                        <Label className="text-sm font-medium">
                            Billing Address <span className="text-red-500">*</span>
                        </Label>

                        {/* Street Address */}
                        <div className="space-y-1.5">
                            <Label htmlFor="streetAddress" className="text-xs text-muted-foreground">
                                Street Address <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="streetAddress"
                                placeholder="123 Business Street"
                                value={formData.streetAddress}
                                onChange={(e) =>
                                    setFormData({ ...formData, streetAddress: e.target.value })
                                }
                            />
                        </div>

                        {/* Unit / Suite / Floor */}
                        <div className="space-y-1.5">
                            <Label htmlFor="unitSuiteFloor" className="text-xs text-muted-foreground">
                                Unit / Suite / Floor
                            </Label>
                            <Input
                                id="unitSuiteFloor"
                                placeholder="e.g. Suite 100, Apt 2B, Floor 3"
                                value={formData.unitSuiteFloor}
                                onChange={(e) =>
                                    setFormData({ ...formData, unitSuiteFloor: e.target.value })
                                }
                            />
                        </div>

                        {/* City and State/Province */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="city" className="text-xs text-muted-foreground">
                                    City <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="city"
                                    placeholder="City"
                                    value={formData.city}
                                    onChange={(e) =>
                                        setFormData({ ...formData, city: e.target.value })
                                    }
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="stateProvince" className="text-xs text-muted-foreground">
                                    State / Province <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="stateProvince"
                                    placeholder="State / Province"
                                    value={formData.stateProvince}
                                    onChange={(e) =>
                                        setFormData({ ...formData, stateProvince: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        {/* Postcode/ZIP and Country */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="postcodeZip" className="text-xs text-muted-foreground">
                                    Postcode / ZIP <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="postcodeZip"
                                    placeholder="94105"
                                    value={formData.postcodeZip}
                                    onChange={(e) =>
                                        setFormData({ ...formData, postcodeZip: e.target.value })
                                    }
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="country" className="text-xs text-muted-foreground">
                                    Country <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                    value={formData.country}
                                    onValueChange={(value) =>
                                        setFormData({ ...formData, country: value })
                                    }
                                >
                                    <SelectTrigger id="country">
                                        <SelectValue placeholder="Select a country" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {COUNTRY_CODES.map((country) => (
                                            <SelectItem
                                                key={country.code}
                                                value={country.name}
                                                onClick={() =>
                                                    setFormData({ ...formData, country: country.name })
                                                }
                                            >
                                                {country.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Subscription Plan Details */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">Subscription Plan Details</Label>
                        <div className="grid grid-cols-3 gap-4">
                            {/* Payment Currency */}
                            <div className="space-y-1.5">
                                <Label htmlFor="invoiceCurrency" className="text-xs text-muted-foreground">
                                    Payment Currency <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                    value={formData.invoiceCurrency}
                                    onValueChange={(value) =>
                                        setFormData({ ...formData, invoiceCurrency: value })
                                    }
                                >
                                    <SelectTrigger id="invoiceCurrency">
                                        <SelectValue placeholder="Select currency" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="MYR">MYR</SelectItem>
                                        <SelectItem value="SGD">SGD</SelectItem>
                                        <SelectItem value="IDR">IDR</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Selected Plan */}
                            <div className="space-y-1.5">
                                <Label htmlFor="subscriptionPlan" className="text-xs text-muted-foreground">
                                    Selected Plan <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                    value={formData.subscriptionPlan}
                                    onValueChange={(value) =>
                                        setFormData({ ...formData, subscriptionPlan: value })
                                    }
                                >
                                    <SelectTrigger id="subscriptionPlan">
                                        <SelectValue placeholder="Select plan" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {packages.map((pkg) => (
                                            <SelectItem key={pkg.id} value={pkg.id}>
                                                {pkg.package_name}
                                                {pkg.platform && (
                                                    <span className="text-gray-400">
                                                        {" "}· {PLAN_PLATFORM_LABEL[pkg.platform] ?? pkg.platform}
                                                    </span>
                                                )}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Start Date */}
                            <div className="space-y-1.5">
                                <Label htmlFor="startDate" className="text-xs text-muted-foreground">
                                    Start Date <span className="text-red-500">*</span>
                                </Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id="startDate"
                                            variant="outline"
                                            className={cn(
                                                "w-full justify-start text-left font-normal",
                                                !formData.startDate && "text-muted-foreground"
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {formData.startDate ? (
                                                format(formData.startDate, "d MMMM yyyy")
                                            ) : (
                                                <span>Pick a date</span>
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={formData.startDate}
                                            onSelect={(date) =>
                                                setFormData({ ...formData, startDate: date })
                                            }
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Invoice Number — optional; sent as "" when empty */}
                            <div className="space-y-1.5">
                                <Label htmlFor="invoiceNumber" className="text-xs text-muted-foreground">
                                    Invoice Number
                                </Label>
                                <Input
                                    id="invoiceNumber"
                                    value={formData.invoiceNumber}
                                    onChange={(e) =>
                                        setFormData({ ...formData, invoiceNumber: e.target.value })
                                    }
                                    placeholder="e.g. INV-2026-0001"
                                />
                            </div>

                            {/* Invoice Date — optional; sent as "" when unset */}
                            <div className="space-y-1.5">
                                <Label htmlFor="invoiceDate" className="text-xs text-muted-foreground">
                                    Invoice Date
                                </Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id="invoiceDate"
                                            variant="outline"
                                            className={cn(
                                                "w-full justify-start text-left font-normal",
                                                !formData.invoiceDate && "text-muted-foreground"
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {formData.invoiceDate ? (
                                                format(formData.invoiceDate, "d MMMM yyyy")
                                            ) : (
                                                <span>Pick a date</span>
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={formData.invoiceDate}
                                            onSelect={(date) =>
                                                setFormData({ ...formData, invoiceDate: date })
                                            }
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>

                        {/* Subscription Paid Toggle */}
                        <div className="flex items-center gap-3 pt-2">
                            <Switch
                                id="isSubscriptionPaid"
                                checked={formData.isSubscriptionPaid}
                                className="bg-blue-600 data-[state=checked]:bg-blue-600"
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, isSubscriptionPaid: checked })
                                }
                            />
                            <Label htmlFor="isSubscriptionPaid" className="text-sm">
                                Subscription Paid
                            </Label>
                            <TooltipProvider>
                                <TooltipRoot>
                                    <TooltipTrigger asChild>
                                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                        <p>Enable this if the user has already paid for the subscription plan before the invoice is created.</p>
                                    </TooltipContent>
                                </TooltipRoot>
                            </TooltipProvider>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700 text-white">
                        {isSubmitting ? "Activating..." : "Activate"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
